import { axios } from '@/lib/helpers';
import { createAction } from '@reduxjs/toolkit';

export const productRequestStarted = createAction('product/request/started');
export const productRequestFailed = createAction('product/request/failed');
export const productRequestCompleted = createAction('product/request/completed');

export const setProduct = createAction('product/set');
export const setBalancesTableColumns = createAction('product/balances-table-columns/set');

export const fetchProductSucceeded = createAction('product/product-fetch/succeeded');
export const fetchProduct = (productUuid, cb) => (dispatch, getState) => {
  dispatch(productRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'get',
    crossdomain: true,
    url: `${chainUrls.GET_PRODUCT}uuid=${productUuid}`,
  }).then(
    ({ data }) => {
      dispatch(fetchProductSucceeded(data.result));
      if (cb) {
        cb();
      }
    },
    ({ message }) => dispatch(productRequestFailed(message))
  );
};

export const updateProductSucceeded = createAction('product/product-update/succeeded');
export const updateProduct = (productUuid, updateData, cb) => (dispatch, getState) => {
  dispatch(productRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'put',
    data: updateData,
    url: `${chainUrls.PUT_PRODUCT}uuid=${productUuid}`,
  }).then(
    ({ data }) => {
      dispatch(updateProductSucceeded(data.result));
      if (cb) {
        cb();
      }
    },
    ({ message }) => dispatch(productRequestFailed(message))
  );
};

// PRODUCT PRICING
export const productPricingsRequestStarted = createAction('product-pricings/request/started');
export const productPricingsRequestFailed = createAction('product-pricings/request/failed');

export const postProductPricingSucceeded = createAction('product-pricings/put/succeeded');
export const postProductPricing = (sapId, createData, cb) => (dispatch, getState) => {
  dispatch(productPricingsRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'post',
    data: { createData },
    url: `${chainUrls.POST_PRICING}sap_id=${sapId}`,
  }).then(
    ({ data }) => {
      dispatch(postProductPricingSucceeded({ product: data.result.product }));
      cb({ uuid: data.result.createdUuid, type: createData.saleType });
    },
    ({ message }) => dispatch(productPricingsRequestFailed(message))
  );
};

export const editProductPricingSucceeded = createAction('product-pricings/put/succeeded');
export const editProductPricing = (sapId, requestData) => (dispatch, getState) => {
  dispatch(productPricingsRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'put',
    data: requestData,
    url: `${chainUrls.PUT_PRICING}sap_id=${sapId}`,
  }).then(
    ({ data }) => dispatch(editProductPricingSucceeded({ product: data.result.product })),
    ({ message }) => dispatch(productPricingsRequestFailed(message))
  );
};

export const deleteProductPricingSucceeded = createAction('product-pricings/delete/succeeded');
export const deleteProductPricing = (sapId, requestData) => (dispatch, getState) => {
  dispatch(productPricingsRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'delete',
    data: requestData,
    url: `${chainUrls.DELETE_PRICING}sap_id=${sapId}`,
  }).then(
    ({ data }) => dispatch(deleteProductPricingSucceeded({ product: data.result })),
    ({ message }) => dispatch(productPricingsRequestFailed(message))
  );
};

// PRODUCT PRICING COMPARISON
export const productPricingComparisonRequestStarted = createAction(
  'product-pricing-comparison/request/started'
);
export const productPricingComparisonRequestFailed = createAction(
  'product-pricing-comparison/request/failed'
);

export const addProductPricingComparisonSucceeded = createAction(
  'product-pricings/delete/succeeded'
);
export const addProductPricingComparison = (requestData) => (dispatch, getState) => {
  dispatch(productPricingComparisonRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'put',
    data: requestData,
    url: `${chainUrls.PUT_PRICING_COMPARISON}`,
  }).then(
    ({ data }) => dispatch(addProductPricingComparisonSucceeded({ product: data.result })),
    ({ message }) => dispatch(productPricingComparisonRequestFailed(message))
  );
};

// PRODUCT MEDIA
export const productMediaRequestStarted = createAction('product-media/request/started');
export const productMediaRequestFailed = createAction('product-media/request/failed');
export const productMediaRequestSucceeded = createAction('product-media/request/succeeded');
export const editProductMedia = (sapId, media, productUuid, cb) => (dispatch, getState) => {
  dispatch(productMediaRequestStarted());

  const state = getState();
  const userState = state.user;
  let { activeChain, chainUrls } = userState;

  return axios({
    method: 'put',
    data: { media },
    url: `${chainUrls.PUT_PRODUCT_MEDIA}?sapId=${sapId}&productUuid=${productUuid}&chain=${activeChain}`,
  }).then(
    ({ data }) => {
      dispatch(productMediaRequestSucceeded({ product: data.result }));
      if (cb) {
        cb();
      }
    },
    ({ message }) => dispatch(productMediaRequestFailed(message))
  );
};

export const copyProductMedia = (productUuid, requestData, callback) => (dispatch, getState) => {
  dispatch(productMediaRequestStarted());

  const state = getState();
  const userState = state.user;
  let { activeChain, chainUrls } = userState;

  return axios({
    method: 'put',
    data: requestData,
    url: `${chainUrls.COPY_PRODUCT_MEDIA}?productUuid=${productUuid}&chain=${activeChain}`,
  }).then(
    ({ data }) => {
      dispatch(productMediaRequestSucceeded({ product: data.result }));
      callback();
    },
    ({ message }) => dispatch(productMediaRequestFailed(message))
  );
};

// PRODUCT MESSAGES
export const postProductMessageSucceeded = createAction('product/messages/post/succeeded');
export const postProductMessage = (requestData, productUuid) => (dispatch, getState) => {
  dispatch(productRequestStarted());

  const currentState = getState();
  const userState = currentState.user;
  let { user, chainUrls } = userState;

  return axios({
    method: 'post',
    data: { ...requestData, userId: user.uuid },
    url: `${chainUrls.POST_PRODUCT_MESSAGE}productUuid=${productUuid}`,
  }).then(
    ({ data }) => dispatch(postProductMessageSucceeded({ product: data.result })),
    ({ message }) => dispatch(productRequestFailed(message))
  );
};

export const deleteProductMessageSucceeded = createAction('product/messages/delete/succeeded');
export const deleteProductMessage = (messageUuid, productUuid) => (dispatch, getState) => {
  dispatch(productRequestStarted());

  const state = getState();
  const userState = state.user;
  let { chainUrls } = userState;

  return axios({
    method: 'delete',
    data: { messageUuid },
    url: `${chainUrls.DELETE_PRODUCT_MESSAGE}productUuid=${productUuid}`,
  }).then(
    ({ data }) => dispatch(deleteProductMessageSucceeded({ product: data.result })),
    ({ message }) => dispatch(productRequestFailed(message))
  );
};
